/**
 * Test component for use-hapimcp adapter
 * Use this to verify the adapter works before integrating into ChatWindow
 * 
 * Usage:
 * 1. Import this in App.tsx temporarily
 * 2. Replace ChatWindow with ChatWindowTest
 * 3. Test basic message flow
 * 4. Check browser console for logs
 * 5. Once verified, remove and integrate into real ChatWindow
 */

import { useState } from 'react';
import { useMcpChatAdapter } from '@/hooks/useMcpChatAdapter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import type { MCPServer, ChatMessage, LLMSettings } from '@/types';

interface ChatWindowTestProps {
  contact: MCPServer;
  llmSettings: LLMSettings;
}

export const ChatWindowTest = ({ contact, llmSettings }: ChatWindowTestProps) => {
  const [input, setInput] = useState('');
  const [persistedMessages, setPersistedMessages] = useState<ChatMessage[]>([]);

  const {
    messages,
    sendMessage,
    status,
    error,
    appTools,
    actions,
    cancel,
  } = useMcpChatAdapter({
    contact,
    initialMessages: persistedMessages,
    llmSettings,
    onMessagesChange: (msgs) => {
      console.log('[Test] Messages updated:', msgs.length);
      setPersistedMessages(msgs);
    },
    onAction: (action) => {
      console.log('[Test] Agent action:', action);
    },
  });

  const handleSend = async () => {
    if (!input.trim()) return;
    console.log('[Test] Sending message:', input);
    await sendMessage(input);
    setInput('');
  };

  const isSending = status === 'sending';

  return (
    <Card className="p-4 max-w-2xl mx-auto mt-8">
      <div className="space-y-4">
        {/* Header */}
        <div className="border-b pb-2">
          <h2 className="text-lg font-semibold">{contact.name}</h2>
          <div className="text-sm text-gray-600 space-y-1">
            <p>Status: <span className="font-mono">{status}</span></p>
            <p>Tools: {appTools.length}</p>
            <p>Messages: {messages.length}</p>
            <p>Actions: {actions.length}</p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-red-800 text-sm font-medium">Error:</p>
            <p className="text-red-600 text-sm">{error.message}</p>
          </div>
        )}

        {/* Tools Display */}
        {appTools.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-blue-800 text-sm font-medium mb-2">Available Tools:</p>
            <ul className="text-sm text-blue-600 space-y-1">
              {appTools.map((tool) => (
                <li key={tool.id || tool.name} className="font-mono">
                  {tool.name} {!tool.enabled && '(disabled)'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Messages Display */}
        <div className="space-y-2 min-h-[200px] max-h-[400px] overflow-y-auto">
          {messages.length === 0 && (
            <p className="text-gray-400 text-center py-8">No messages yet. Send one below!</p>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`p-3 rounded ${
                msg.author === 'user'
                  ? 'bg-blue-100 ml-8'
                  : msg.author === 'agent'
                    ? 'bg-gray-100 mr-8'
                    : 'bg-yellow-50'
              }`}
            >
              <div className="flex justify-between items-start mb-1">
                <span className="text-xs font-medium capitalize">{msg.author}</span>
                <span className="text-xs text-gray-500">{msg.status}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              {msg.tags && msg.tags.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {msg.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-white px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions Log */}
        {actions.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded p-3">
            <p className="text-purple-800 text-sm font-medium mb-2">Agent Actions:</p>
            <ul className="text-xs text-purple-600 space-y-1">
              {actions.map((action) => (
                <li key={action.id} className="font-mono">
                  {action.action.name}: {action.status}
                  {action.duration && ` (${action.duration}ms)`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Input */}
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            disabled={isSending}
          />
          <Button onClick={handleSend} disabled={isSending || !input.trim()}>
            {isSending ? 'Sending...' : 'Send'}
          </Button>
          {isSending && (
            <Button onClick={cancel} variant="outline">
              Cancel
            </Button>
          )}
        </div>

        {/* Debug Info */}
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600">Debug Info</summary>
          <pre className="mt-2 p-2 bg-gray-50 rounded overflow-x-auto">
            {JSON.stringify(
              {
                status,
                messageCount: messages.length,
                toolCount: appTools.length,
                actionCount: actions.length,
                lastMessage: messages[messages.length - 1],
                error: error?.message,
              },
              null,
              2
            )}
          </pre>
        </details>
      </div>
    </Card>
  );
};
